<script lang="ts" setup>
import type { Product } from '#libs/shopify'
import { extractIdFromGraphqlId, FINAL_SALE_TAGS } from '#libs/shopify'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { ProductQuickAddProps } from '.'
import { fetchProductByHandle } from './product-quick-add.utils'

type SizeOption = {
  value: string
  variantGraphqlId: string
  variantId: string
  available: boolean
}

const { handle } = defineProps<{
  handle: ProductQuickAddProps['handle']
}>()
const innerEl = ref<HTMLElement | null>(null)
const isOptionsShowed = ref<boolean>(false)
const isAddingToCart = ref<boolean>(false)
const product = ref<Product | null>(null)
const sizeOptions = computed<SizeOption[]>(() => {
  if (!product.value || !product.value.variants) return []

  return (product.value.variants.nodes || []).map((variant) => {
    const sizeOption = variant.selectedOptions.find((option) => option.name.toLowerCase() === 'size')

    return {
      value: sizeOption ? sizeOption.value : '',
      variantGraphqlId: variant.id,
      variantId: extractIdFromGraphqlId(variant.id),
      available: variant.availableForSale
    } as SizeOption
  })
})
const isFinalSale = computed<boolean>(() => {
  if (!product.value || !product.value.tags.length) return false

  return product.value.tags.some((t) => FINAL_SALE_TAGS.includes(t))
})
const maybeFetchProduct = async () => {
  if (product.value) return

  product.value = await fetchProductByHandle(handle)
}
const showOptions = () => {
  isOptionsShowed.value = true
}
const resetState = () => {
  isOptionsShowed.value = false
}
const handleDocumentClick = (event: MouseEvent) => {
  if (!innerEl.value || innerEl.value.contains(event.target as Node)) return

  resetState()
}
const addToCart = async (sizeOption: SizeOption) => {
  try {
    if (window.pn) {
      const singleProductSettings = window.pn.settings.singleProductSettings

      const variant = singleProductSettings.find((variant) => variant.n === String(sizeOption.variantId))

      window.__GLOBAL__.preOrderMessage = <string>variant?.g
    }
    isAddingToCart.value = true
    isOptionsShowed.value = false

    const cartEndpoint = '/cart/add.js'
    let sectionsToBundle: string[] = []

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    )

    const properties = {
      ...(isFinalSale.value ? { Note: window.__GLOBAL__.finalSaleMessage || 'This item is final sale' } : {}),
      ...(window.__GLOBAL__.preOrderMessage ? { 'PN-Note': window.__GLOBAL__.preOrderMessage } : {})
    }

    const items = [{ id: sizeOption.variantId, quantity: 1, properties }]
    const addToCartResponse = await fetch(cartEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items, sections: sectionsToBundle })
    })
    const addToCartResult = await addToCartResponse.json()
    const cartContent = await (await fetch(`/cart.js`)).json()

    cartContent['sections'] = addToCartResult['sections']
    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: 'force_open_drawer',
          cart: cartContent
        }
      })
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to add to cart "${sizeOption.variantId}"!`, error)
  } finally {
    isAddingToCart.value = false
  }
}

onMounted(async () => {
  const closestFigureEl = innerEl.value ? innerEl.value.closest('.js-product-card-figure') : null

  closestFigureEl &&
    closestFigureEl.addEventListener('mouseleave', () => {
      resetState()
    })

  document.addEventListener('click', handleDocumentClick)
  maybeFetchProduct()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <div
    ref="innerEl"
    class="product-quick-add__inner"
    :class="{ 'show-options': isOptionsShowed, 'adding-to-cart': isAddingToCart }"
  >
    <button class="product-quick-add__button" type="button" aria-label="Quick add" @click.prevent="showOptions">
      <span class="product-quick-add__button-text">Quick Add</span>
    </button>
    <div class="product-quick-add__options">
      <template v-for="sizeOption in sizeOptions">
        <button
          v-if="sizeOption.value"
          :key="handle + '-size-option-' + sizeOption.variantId"
          class="product-quick-add__option"
          type="button"
          :disabled="!sizeOption.available"
          @click.prevent="addToCart(sizeOption)"
        >
          {{ sizeOption.value }}
        </button>
      </template>
    </div>
  </div>
</template>
